import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { background } from 'assets/backgrounds'
import { Button } from 'cwp-components/forms/Buttons'
import humanize from 'humanize'
import useWhoWeAre from 'common/providers/useWhoWeAre'
import Constants from 'common/utils/constants'
import { AppState } from 'common/state-type'
import { BsFillCaretLeftFill, BsFillCaretRightFill } from 'react-icons/bs'
import { useNavigate } from 'react-router-dom'

const pageSize = 4
const DATE_FORMAT = 'jS M, y'

const LawTech = () => {
  const { whoweare, getWhoWeAre } = useWhoWeAre()
  const [page, setPage] = useState(1)
  const isLoadingMore = useRef(false)

  const [previousData, setPreviousData] = useState<
    AppState['legalDocuments']['results']
  >([])

  const navigate = useNavigate()

  const loadWhoWeAre = useCallback(() => {
    getWhoWeAre(
      {
        is_template: Constants.isAvvokaEnabled,
        page,
        page_size: pageSize,
      },
      {
        onSuccess: () => {
          isLoadingMore.current = false
        },
        onError: () => {
          isLoadingMore.current = false
        },
      },
    )
  }, [page, pageSize, getWhoWeAre])

  useEffect(loadWhoWeAre, [loadWhoWeAre])

  const swipeData = useCallback(
    (key) => () => {
      if (!whoweare?.[key] || isLoadingMore.current) {
        return
      }
      isLoadingMore.current = true
      key === 'next' ? setPage(page + 1) : setPage(page - 1)
    },
    [whoweare, page],
  )

  const loadMore = useCallback(() => {
    if (!whoweare?.next || isLoadingMore.current) {
      return
    }

    isLoadingMore.current = true
    setPreviousData([...previousData, ...(whoweare?.results ?? [])])
    setPage(page + 1)
  }, [whoweare, page])

  const results = useMemo(() => {
    const docs = [...previousData, ...(whoweare?.results ?? [])]

    // Check for uniqueness based on ID, just in case.
    return docs.filter(
      (value, index, self) =>
        index === self.findIndex(({ id }) => id === value.id),
    )
  }, [previousData, whoweare])

  return (
    <div id='lawtech' className='relative my-[300px]'>
      <img
        src={background.WhoWeAre.CurveRight}
        className='absolute right-0 md:top-[-300px] top-[-20px] z-[-1] md:w-[200px] w-[100px]'
        alt=''
      />
      <div className=' lg:mx-[270px] mx-[30px]'>
        <h1 className='my-4 md:text-[40px] text-center font-bold text-primary '>
          Lawtech Around the World
        </h1>

        <div className='grid auto-rows-fr md:grid-cols-2 lg:grid-cols-4  grid-cols-1 md:gap-[30px] gap-[20px]'>
          {results.map((item, idx) => (
            <div className='card relative' key={`whoweare_${idx}`}>
              <div
                className='card-cover'
                style={{
                  background: `url(${item.cover_image}) lightgray 50% / cover no-repeat`,
                }}
              ></div>
              <div className='absolute top-[21px] left-[18px]'>
                <span className='badge bg-white text-[12px]'>
                  {humanize.date(DATE_FORMAT, new Date(item.publish_date))}
                </span>
              </div>
              <div className='p-[30px_20px_16px_20px] text-start !z-[1] relative'>
                <div className='absolute right-[20px] top-[-20px]  h-[40px] p-[4px] rounded-[30px] bg-[#EBFBFF] flex items-center gap-1'>
                  <img
                    className='rounded-full h-[24px] w-[24px]'
                    style={{
                      background: `url(${item.author.avatar}) lightgray 50% / cover no-repeat`,
                    }}
                  ></img>
                  <p className='text-[10px]'> {item.author.name}</p>
                </div>
                <div className='flex flex-col h-full flex-1'>
                  <h4 className='text-[14px] font-medium'>{item.title}</h4>
                  <p className='mt-[6px] text-[12px] text-[#6E7785]'>
                    {item.summary.substring(0, 150)}
                  </p>
                  <div className='mt-auto flex justify-end'>
                    <Button
                      onClick={() => navigate(`/blogs/${item.slug}`)}
                      className='btn-default mt-4 text-[#1C7D93]'
                    >
                      View
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* </Slider> */}
        <div className='max-sm:hidden flex justify-end gap-[35px] mt-[40px]'>
          <BsFillCaretLeftFill
            size={32}
            className={`${
              whoweare?.previous
                ? 'cursor-pointer'
                : 'cursor-not-allowed text-text-muted'
            }`}
            onClick={swipeData('previous')}
          />
          <BsFillCaretRightFill
            size={32}
            className={`${
              whoweare?.next
                ? 'cursor-pointer'
                : 'cursor-not-allowed text-text-muted'
            }`}
            onClick={swipeData('next')}
          />
        </div>
        {!!whoweare?.next && (
          <div className='md:hidden flex items-center justify-end mt-8'>
            <a
              type='button'
              className='text-[14px] underline font-medium cursor-pointer'
              onClick={loadMore}
            >
              Show more
            </a>
          </div>
        )}
      </div>
    </div>
  )
}

export default LawTech
